import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ApiCallService from "../../../Service/ApiCallService";

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "video/mp4",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const schema = yup.object({
  foundationProgram: yup.string().required("Foundation program is required"),
  programDate: yup.string().required("Program date is required"),
  location: yup.string().trim().required("Location is required"),
  description: yup.string().trim().min(20, "Add a short program summary").required("Description is required"),
});

const initialForm = {
  foundationProgram: "",
  programDate: "",
  location: "",
  description: "",
};

const VibrancySubmissionForm = ({ open, chapter, onClose, onSubmitted }) => {
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [errors, setErrors] = useState({});
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!open) return;

    setForm(initialForm);
    setFiles([]);
    setErrors({});
    setSubmitError("");

    const loadPrograms = async () => {
      try {
        setLoadingPrograms(true);
        const response = await ApiCallService.get(
          `${process.env.REACT_APP_BASE_URL}/vibrancy-foundation-programs`,
        );
        setPrograms(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to load foundation programs:", error);
        setSubmitError("Unable to load foundation programs.");
      } finally {
        setLoadingPrograms(false);
      }
    };

    loadPrograms();
  }, [open]);

  const chapterLocation = useMemo(() => {
    const location = chapter?.location || {};
    return [
      location.district?.districtName || location.district?.name,
      location.state?.stateName || location.state?.name,
    ]
      .filter(Boolean)
      .join(", ");
  }, [chapter]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFiles = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const invalidFile = selectedFiles.find(
      (file) => !ALLOWED_TYPES.includes(file.type) || file.size > MAX_FILE_SIZE,
    );

    if (invalidFile) {
      setErrors((prev) => ({
        ...prev,
        files: "Only jpg, jpeg, png, mp4, pdf, doc and docx files up to 25 MB are allowed.",
      }));
      return;
    }

    setFiles(selectedFiles);
    setErrors((prev) => ({ ...prev, files: undefined }));
  };

  const validate = async () => {
    try {
      await schema.validate(form, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationError) {
      const nextErrors = {};
      validationError.inner.forEach((item) => {
        nextErrors[item.path] = item.message;
      });
      setErrors(nextErrors);
      return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");
    const isValid = await validate();
    if (!isValid || !chapter?._id) return;

    const formData = new FormData();
    formData.append("chapter", chapter._id);
    formData.append("foundationProgram", form.foundationProgram);
    formData.append("programDate", form.programDate);
    formData.append("location", form.location);
    formData.append("description", form.description);
    files.forEach((file) => formData.append("attachments", file));

    try {
      setSubmitting(true);
      await ApiCallService.post(
        `${process.env.REACT_APP_BASE_URL}/vibrancy-submissions`,
        formData,
      );
      onSubmitted?.();
      onClose?.();
    } catch (error) {
      console.error("Failed to submit vibrancy request:", error);
      setSubmitError(error?.response?.data?.message || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="md">
      {/* <DialogTitle sx={{ fontWeight: 800 }}>Initiate Vibrancy</DialogTitle> */}
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <TextField
            label="Chapter"
            value={chapter?.chapterName || ""}
            InputProps={{ readOnly: true }}
            helperText={chapterLocation || "Prospective chapter"}
            fullWidth
          />

          <FormControl fullWidth error={Boolean(errors.foundationProgram)}>
            <InputLabel>Foundation Program</InputLabel>
            <Select
              name="foundationProgram"
              label="Foundation Program"
              value={form.foundationProgram}
              onChange={handleChange}
              disabled={loadingPrograms}
            >
              {programs.map((program) => (
                <MenuItem key={program._id} value={program._id}>
                  {program.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.foundationProgram || (loadingPrograms ? "Loading programs..." : "Only Foundation basket programs are shown")}
            </FormHelperText>
          </FormControl>

          <TextField
            name="programDate"
            label="Program Date"
            type="date"
            value={form.programDate}
            onChange={handleChange}
            error={Boolean(errors.programDate)}
            helperText={errors.programDate}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          <TextField
            name="location"
            label="Location / Venue"
            value={form.location}
            onChange={handleChange}
            error={Boolean(errors.location)}
            helperText={errors.location}
            fullWidth
          />

          <TextField
            name="description"
            label="Description / Program Summary"
            value={form.description}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            minRows={4}
            fullWidth
          />

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderStyle: "dashed",
              borderColor: errors.files ? "error.main" : "divider",
              bgcolor: "#fafafa",
            }}
          >
            <Stack spacing={1.5} alignItems="center">
              <CloudUploadIcon color="primary" />
              <Typography variant="subtitle2" fontWeight={700}>
                Upload Media & Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                jpg, jpeg, png, mp4, pdf, doc and docx. Multiple files supported.
              </Typography>
              <Button component="label" variant="outlined">
                Select Files
                <input hidden multiple type="file" onChange={handleFiles} />
              </Button>
              {files.length > 0 && (
                <Box sx={{ width: "100%" }}>
                  {files.map((file) => (
                    <Typography key={`${file.name}-${file.size}`} variant="body2" color="text.secondary">
                      {file.name}
                    </Typography>
                  ))}
                </Box>
              )}
              {errors.files && (
                <Typography variant="caption" color="error">
                  {errors.files}
                </Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VibrancySubmissionForm;
